import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import { PageToolbar } from "@/components/PageToolbar/PageToolbar";
import { SplitPane } from "@/components/SplitPane/SplitPane";
import styles from "./Shifts.module.scss";
import { useScheduledShifts } from "@/api/Shifts";
import {
  employeeName,
  shiftTypesDictionary,
  unusualShiftTypes,
  type IEmployee,
  type IMedicalCompetence,
  type IShiftType,
} from "@medoma/models/shifts";
import { type ITimeOfDay } from "@medoma/models/activities";
import { Trans, useLingui } from "@lingui/react/macro";
import { useLingui as useLinguiNonMacro } from "@lingui/react";
import { useMemo } from "react";
import { useSelectedDate } from "@/Utils/useSelectedDate";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";
import { getDataFromShifts } from "./getDataFromShifts";
import * as Sentry from "@sentry/react";
import { deducedError } from "@/Utils/ErrorUtils";
import {
  Table,
  TableHeader,
  Column,
  TableBody,
  Row,
  Cell,
} from "react-aria-components";
import { Outlet, useNavigate, useParams } from "react-router";
import { Heading } from "@medoma/components/Heading/Heading";
import { ShiftDisplayName } from "./ShiftsDisplayName";
import { ShiftTime } from "@/components/Time/ShiftTime";
import clsx from "clsx";
import { Text } from "@medoma/components/Text/Text";
import { PlainButton } from "@medoma/components/Button/Button";
import PlusIcon from "@medoma/components/icons/PlusIcon";
import type { ITeam } from "@medoma/models/patients";
import TeamBadge from "@/components/Teams/TeamBadge";
import { filterByNameTerms } from "@medoma/components/SearchField/searchUtils";
import { useEmployeeNameFilter } from "./ShiftsContext";
import {
  ShiftsContextProvider,
  ShiftsFilterInput,
} from "./ShiftsContextComponents";
import { ShiftsFilterBannerSection } from "./ShiftsFilterBannerSection";

type ShiftTableItem = {
  id: string;
  competence: IMedicalCompetence;
  timespan: [Date, Date];
  employee: IEmployee | null;
  team: ITeam | null;
  shiftType: IShiftType;
  shiftRowIsActive: boolean;
};

const COLUMN_DISPLAY_NAMES = {
  competence: msg`Kompetens`,
  timespan: msg`Tid`,
  employee: msg({ message: `Medarbetare`, context: "singular" }),
  shiftType: msg`Typ`,
};

const columnHelper = createColumnHelper<ShiftTableItem>();

const columns = (translator: I18n["_"]) => [
  columnHelper.accessor("competence", {
    header: translator(COLUMN_DISPLAY_NAMES["competence"]),
    cell: ({ getValue, row }) => {
      return (
        <CompetenceChip
          competence={getValue()}
          state={row.original.employee ? "neutral" : "unassigned"}
          size="medium"
        />
      );
    },
  }),
  columnHelper.accessor("timespan", {
    header: translator(COLUMN_DISPLAY_NAMES["timespan"]),
    cell: ({ getValue }) => {
      const [start, end] = getValue();
      return <ShiftTime startDateTime={start} endDateTime={end} />;
    },
    sortingFn: (a, b) => {
      const aState = a.getValue<[Date, Date, ITimeOfDay]>("timespan");
      const bState = b.getValue<[Date, Date, ITimeOfDay]>("timespan");

      // If we don't have a timespan, we can't sort by it
      if (!aState || !bState) {
        return 0;
      }

      // Any time of day -> end of list
      if (aState[2] === "Any") {
        return 1;
      }
      // Any time of day -> end of list
      if (bState[2] === "Any") {
        return 1;
      }
      if (aState[0] < bState[0]) {
        return -1;
      } else if (aState[0] > bState[0]) {
        return 1;
      } else if (aState[1] < bState[1]) {
        return -1;
      } else if (aState[1] > bState[1]) {
        return 1;
      } else {
        return 0;
      }
    },
  }),
  columnHelper.accessor("employee", {
    header: translator(COLUMN_DISPLAY_NAMES["employee"]),
    cell: ({ getValue, row }) => {
      const employee = getValue();
      const { team } = row.original;
      return (
        <div className={styles.employeeCell}>
          {employee ? (
            <ShiftDisplayName
              assigned={Boolean(employee)}
              employeeName={employeeName(employee, {
                employeeNameFormat: "full",
              })}
            />
          ) : (
            <ShiftDisplayName assigned={false} />
          )}
          {team ? <TeamBadge team={team} size="small" /> : null}
        </div>
      );
    },
  }),
  columnHelper.accessor("shiftType", {
    header: translator(COLUMN_DISPLAY_NAMES["shiftType"]),
    cell: ({ getValue }) => {
      const shiftType = getValue();
      if (!unusualShiftTypes[shiftType]) {
        return null;
      }

      return (
        <Text element="div" color="faded">
          {translator(shiftTypesDictionary[shiftType])}
        </Text>
      );
    },
  }),
];

const ShiftsTable = () => {
  const navigate = useNavigate();
  const { _ } = useLinguiNonMacro();
  const { t } = useLingui();
  const selectedDate = useSelectedDate();
  const { value: employeeNameFilter } = useEmployeeNameFilter();
  const {
    data: shifts,
    isPending,
    isError,
    error,
  } = useScheduledShifts(selectedDate, selectedDate);

  const params = useParams();
  const shiftIdFromParams = params.shiftId;

  const unassignedLabel = t`Obemannad`;
  const data = useMemo(() => {
    const rows = getDataFromShifts({ shifts: shifts || [], shiftIdFromParams });
    if (!employeeNameFilter) {
      return rows;
    }
    return rows.filter(({ employee }) => {
      const searchable = employee
        ? employeeName(employee, { employeeNameFormat: "full" })
        : unassignedLabel;
      return filterByNameTerms(searchable, employeeNameFilter);
    });
  }, [shifts, shiftIdFromParams, employeeNameFilter, unassignedLabel]);

  const sorting = useMemo(() => [{ id: "timespan", desc: false }], []);
  const translatedColumns = columns(_);
  // Acceptable. We don't use react compiler, we just lint to find incompatibilities, such as this.
  // oxlint-disable-next-line react/incompatible-library
  const table = useReactTable({
    data,
    columns: translatedColumns,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
    },
  });

  if (isError) {
    Sentry.captureException(error);
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta arbetspassen.`} ${deducedError(error)}`}
        padding={24}
      />
    );
  }

  if (isPending) {
    return <Loading message={t`Hämtar arbetspass`} padding={24} />;
  }

  return (
    <Table
      aria-label={t({ message: `Medarbetare`, context: "plural" })}
      className={styles.table}
      key={selectedDate.toDateString()}
    >
      <TableHeader className={styles.tableHeader}>
        {table.getHeaderGroups().map((headerGroup) =>
          headerGroup.headers.map((header) => {
            return <Column key={header.id} isRowHeader />;
          }),
        )}
      </TableHeader>
      <TableBody items={table.getRowModel().rows}>
        {(row) => {
          return (
            <Row
              id={row.id}
              key={row.id}
              className={clsx([
                styles.tableRow,
                row.original.shiftRowIsActive && styles.activeRow,
              ])}
              onAction={() => {
                void navigate(row.id);
              }}
            >
              {row.getVisibleCells().map((cell) => {
                return (
                  <Cell key={cell.id} className={styles.tableCell}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Cell>
                );
              })}
            </Row>
          );
        }}
      </TableBody>
    </Table>
  );
};

export const Shifts = () => {
  const { t } = useLingui();
  const navigate = useNavigate();

  return (
    <ShiftsContextProvider>
      <PageToolbar showDaySwitcher>
        <div className={styles.tools}>
          <ShiftsFilterInput label={t`Sök medarbetare`} />
          <PlainButton onClick={() => navigate("new")}>
            <PlusIcon />
            <Trans>Skapa arbetspass</Trans>
          </PlainButton>
        </div>
      </PageToolbar>
      <ShiftsFilterBannerSection />
      <div className={styles.shiftsContainer}>
        <SplitPane panelSizeLocalStorageKey="shifts">
          {/* Elements spawn from ShiftsTable, messing with SplitPane child styles, requires div in between */}
          <div>
            <Heading level="h1" className={styles.pageHeader}>
              <Trans>Bemanning</Trans>
            </Heading>
            <ShiftsTable />
          </div>
          <div className={styles.detailsPanel}>
            <Outlet />
          </div>
        </SplitPane>
      </div>
    </ShiftsContextProvider>
  );
};
